import { useMemo, useState } from 'react';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { formatDuration, formatStarted } from './format';
import { outcomeOf } from './outcome';
import { OutcomePill } from './OutcomePill';
import type { Run } from './types';
import { slugQuery, useInsights } from './useInsights';

const PAGE_SIZE = 10;

const useStyles = makeStyles(theme => ({
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  workflowSelect: { minWidth: 220 },
  // Fixed layout: the columns share the card's width instead of the widest
  // branch name setting it, so the table never runs past the card's edge.
  table: { tableLayout: 'fixed', width: '100%' },
  cell: {
    padding: theme.spacing(0.75, 1.25),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  branch: { fontFamily: 'var(--font-mono)', fontSize: '0.8rem' },
  compactList: { listStyle: 'none', margin: 0, padding: 0 },
  compactRow: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  compactMeta: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    overflowWrap: 'anywhere',
  },
  source: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
}));

function tookOf(run: Run): string {
  if (run.durationSec !== null) return formatDuration(run.durationSec);
  return outcomeOf(run).kind === 'running' ? 'In progress' : 'Unknown';
}

const Started = ({ run }: { run: Run }) => {
  const started = formatStarted(run.createdAt, Date.now());
  if (!started || !run.createdAt) return <>Unknown</>;
  return (
    <time dateTime={run.createdAt} title={started.exact}>
      {started.label}
    </time>
  );
};

/**
 * The "Recent runs" card (specs/component-pipeline.md AC-6 to AC-12).
 *
 * The filters narrow the runs already loaded rather than asking GitHub again,
 * so an empty result means "none of the last twenty" and says so.
 */
export const RecentRunsCard = ({
  slug,
  defaultBranch,
}: {
  slug: string;
  /** Undefined while unknown: the default-branch toggle is left out. */
  defaultBranch?: string | null;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Below the large breakpoint the content column next to the navigation is
  // too narrow for seven columns, so each run becomes a two-line entry.
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const runs = useInsights<{ total: number; runs: Run[] }>(
    `/workflow-runs?${slugQuery(slug)}`,
    {
      refreshWhile: data =>
        data.runs.some(run => outcomeOf(run).kind === 'running'),
    },
  );

  const [workflow, setWorkflow] = useState('');
  const [onDefault, setOnDefault] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [page, setPage] = useState(0);

  const all = useMemo(() => runs.data?.runs ?? [], [runs.data]);
  const workflows = useMemo(
    () => [...new Set(all.map(run => run.name))].sort(),
    [all],
  );
  const shown = all.filter(
    run =>
      (!workflow || run.name === workflow) &&
      (!onDefault || run.headBranch === defaultBranch) &&
      (!onlyFailed || outcomeOf(run).kind === 'failure'),
  );
  const pageRuns = shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const narrowed = Boolean(workflow || onDefault || onlyFailed);

  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  let body: JSX.Element;
  if (runs.loading) {
    body = <Progress />;
  } else if (runs.error) {
    body = (
      <Typography role="alert">
        Couldn't load the run history from GitHub.
      </Typography>
    );
  } else if (!all.length) {
    body = <Typography>No workflow runs yet for {slug}.</Typography>;
  } else {
    body = (
      <>
        <div className={classes.filters}>
          <TextField
            id={`${slug}-workflow-filter`}
            select
            size="small"
            variant="outlined"
            label="Workflow"
            value={workflow}
            onChange={e => refilter(() => setWorkflow(e.target.value))}
            SelectProps={{ native: true }}
            // A native select always shows a value, so the label must sit
            // above it — otherwise the two are drawn on top of each other.
            InputLabelProps={{ shrink: true }}
            className={classes.workflowSelect}
          >
            <option value="">All workflows</option>
            {workflows.map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </TextField>
          {defaultBranch && (
            <Chip
              label={defaultBranch}
              clickable
              color={onDefault ? 'primary' : 'default'}
              variant={onDefault ? 'default' : 'outlined'}
              aria-pressed={onDefault}
              onClick={() => refilter(() => setOnDefault(v => !v))}
            />
          )}
          <Chip
            label="Failed"
            clickable
            color={onlyFailed ? 'primary' : 'default'}
            variant={onlyFailed ? 'default' : 'outlined'}
            aria-pressed={onlyFailed}
            onClick={() => refilter(() => setOnlyFailed(v => !v))}
          />
        </div>

        {!shown.length && (
          <Typography>
            {narrowed
              ? `None of the last ${all.length} runs match these filters.`
              : `No workflow runs yet for ${slug}.`}
          </Typography>
        )}

        {shown.length > 0 && narrow && (
          <ul className={classes.compactList} aria-label="Recent runs">
            {pageRuns.map(run => (
              <li key={run.id} className={classes.compactRow}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gridGap={8}
                  alignItems="center"
                >
                  <Link to={run.htmlUrl}>{run.name}</Link>
                  <OutcomePill outcome={outcomeOf(run)} />
                </Box>
                <div className={classes.compactMeta}>
                  {[run.headBranch, run.event, run.actor, tookOf(run)]
                    .filter(Boolean)
                    .join(' · ')}
                  {' · '}
                  <Started run={run} />
                </div>
              </li>
            ))}
          </ul>
        )}

        {shown.length > 0 && !narrow && (
          <Table
            size="small"
            aria-label="Recent runs"
            className={classes.table}
          >
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '13%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {[
                  'Workflow',
                  'Branch',
                  'Event',
                  'Actor',
                  'Result',
                  'Duration',
                ].map(title => (
                  <TableCell key={title} className={classes.cell}>
                    {title}
                  </TableCell>
                ))}
                <TableCell align="right" className={classes.cell}>
                  Started
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRuns.map(run => (
                <TableRow key={run.id}>
                  <TableCell className={classes.cell} title={run.name}>
                    <Link to={run.htmlUrl}>{run.name}</Link>
                  </TableCell>
                  <TableCell
                    className={`${classes.cell} ${classes.branch}`}
                    title={run.headBranch ?? undefined}
                  >
                    {run.headBranch ?? 'Unknown'}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {run.event ?? 'Unknown'}
                  </TableCell>
                  <TableCell className={classes.cell} title={run.actor}>
                    {run.actor ?? 'Unknown'}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <OutcomePill outcome={outcomeOf(run)} />
                  </TableCell>
                  <TableCell className={classes.cell}>{tookOf(run)}</TableCell>
                  <TableCell align="right" className={classes.cell}>
                    <Started run={run} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {shown.length > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={shown.length}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[]}
            onPageChange={(_e, next) => setPage(next)}
          />
        )}
      </>
    );
  }

  return (
    <InfoCard
      title="Recent runs"
      action={
        <Box pt={1} pr={1}>
          <Link to={`https://github.com/${slug}/actions`}>
            Open in GitHub Actions
          </Link>
        </Box>
      }
    >
      {body}
      <div className={classes.source}>Source: github-insights · {slug}</div>
    </InfoCard>
  );
};
